import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import type { HeaderNavItem } from '../header/header';

export type AsideVariant = 'default' | 'primary';

const HIDDEN_KEY = 'crm.sidebar.hidden';

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function speechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function readHidden(): ReadonlySet<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

@Component({
  selector: 'aside[ui]',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive],
  templateUrl: './aside.html',
  styleUrls: ['./aside.css', './aside.responsive.css'],
  host: {
    '[attr.data-variant]': 'variant',
    '[attr.aria-label]': 'ariaLabel',
    '[attr.id]': 'id',
    '[attr.data-collapsed]': 'collapsed',
    '[attr.data-overlay-open]': 'overlayOpen',
    '[attr.data-editing]': 'editing() || null',
  },
})
export class AsideComponent {
  @Input() id = 'sidebar';
  @Input() variant: AsideVariant = 'default';
  @Input() ariaLabel = 'Sidebar';
  @Input() navItems: HeaderNavItem[] = [];
  @Input() collapsed = false;
  @Input() overlayOpen = false;

  @Input() primaryNavAriaLabel = 'Primary navigation';
  @Input() searchPlaceholder = 'Search';
  @Input() collapseLabel = 'Collapse';
  @Input() collapseIcon = '‹';
  @Input() expandIcon = '›';
  @Input() expandLabel = 'Expand';
  @Input() unavailableLabel = 'Unavailable';
  @Input() closeSidebarAriaLabel = 'Close sidebar';
  @Input() closeSidebarCharacter = '×';
  @Input() chevronUp = '⌃';
  @Input() editLabel = 'Edit';
  @Input() doneLabel = 'Done';
  @Input() showItemLabel = 'Show in sidebar';
  @Input() voiceSearchLabel = 'Search by voice';
  @Input() listeningLabel = 'Listening…';

  @Output() overlayOpenChange = new EventEmitter<boolean>();
  @Output() collapseToggle = new EventEmitter<void>();
  @Output() searchSubmitted = new EventEmitter<string>();

  /** Edit mode: every item shows a visibility switch; hidden items persist per browser. */
  readonly editing = signal(false);
  readonly hidden = signal<ReadonlySet<string>>(readHidden());
  readonly listening = signal(false);
  protected readonly canListen = speechRecognition() !== null;
  private activeRecognition: SpeechRecognitionLike | null = null;

  /** Section header for this row when it starts a new navGroup among the shown rows. */
  sectionHeader(index: number): string | null {
    const shown = this.shownItems();
    const groupOf = (entry?: HeaderNavItem): string | null => {
      if (entry?.navGroup) return entry.navGroup;
      const section = (entry as { section?: string } | undefined)?.section;
      return section === 'administration' ? 'Administration' : null;
    };
    const current = groupOf(shown[index]);
    if (!current) return null;
    return groupOf(shown[index - 1]) === current ? null : current;
  }

  /** Rows on screen: everything while editing, otherwise the non-hidden items. */
  shownItems(): HeaderNavItem[] {
    return this.editing() ? this.navItems : this.navItems.filter((item) => !this.isHidden(item));
  }

  isHidden(item: HeaderNavItem): boolean {
    return this.hidden().has(item.route);
  }

  visibilityLabel(item: HeaderNavItem): string {
    return `${this.showItemLabel}: ${item.label}`;
  }

  toggleItem(item: HeaderNavItem): void {
    this.hidden.update((set) => {
      const next = new Set(set);
      if (next.has(item.route)) next.delete(item.route);
      else next.add(item.route);
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]));
      } catch {
        // Ignore storage write failures (private browsing / quota exceeded) so UI state still updates.
      }
      return next;
    });
  }

  submitSearch(value: string): void {
    const query = value.trim();
    if (query) this.searchSubmitted.emit(query);
  }

  /** Voice search: fills the field with the transcript and submits it, or stops active recognition on a second click. */
  listen(input: HTMLInputElement): void {
    if (this.listening()) {
      this.activeRecognition?.stop();
      this.activeRecognition = null;
      this.listening.set(false);
      return;
    }
    const Recognition = speechRecognition();
    if (!Recognition) return;
    const recognition = new Recognition();
    this.activeRecognition = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      if (this.activeRecognition !== recognition) return;
      const transcript = event.results[0]?.[0]?.transcript ?? '';
      input.value = transcript;
      this.submitSearch(transcript);
    };
    recognition.onend = () => {
      if (this.activeRecognition !== recognition) return;
      this.activeRecognition = null;
      this.listening.set(false);
    };
    this.listening.set(true);
    recognition.start();
  }

  closeOverlay(): void {
    this.overlayOpen = false;
    this.overlayOpenChange.emit(false);
  }
}
